import * as React from 'react';

import { MultiSelectFilter } from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import {
  $columnSelectedFinancialAccountIds,
  $financialAccountFilterSearchTerm,
  $offset,
  $remoteFilterFinancialAccounts,
} from '~/modules/finance/_state/financePayments.state';
import { useTranslations } from '~/modules/finance/i18n/useTranslations';

interface PaymentFinancialAccountColumnFilterProps {
  onClose?: () => void;
}

//
// ⚛️ ⚛️ ⚛️ PAYMENT FINANCIAL ACCOUNT COLUMN FILTER ⚛️ ⚛️ ⚛️
//
export const PaymentFinancialAccountColumnFilter: React.FC<
  PaymentFinancialAccountColumnFilterProps
> = ({ onClose }) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const {
    data: accountsData,
    isFetching,
    isLoading,
  } = useAtomValue($remoteFilterFinancialAccounts);
  const [selectedIdsSet, setSelectedIdsSet] = useAtom(
    $columnSelectedFinancialAccountIds,
  );
  const setSearchTerm = useSetAtom($financialAccountFilterSearchTerm);
  const setOffset = useSetAtom($offset);

  // Memoized values
  const selectedIds = React.useMemo(
    () => Array.from(selectedIdsSet),
    [selectedIdsSet],
  );

  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedIds),
    [selectedIds],
  );

  const accounts = React.useMemo(
    () => accountsData?.accounts ?? [],
    [accountsData],
  );

  const selectedAccounts = React.useMemo(
    () => accounts.filter((account) => selectedIds.includes(account.id)),
    [accounts, selectedIds],
  );

  // Callbacks
  const handleSearch = React.useCallback(
    (query: string) => {
      setSearchTerm(query || undefined);
    },
    [setSearchTerm],
  );

  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedIdsSet(new Set(Array.from(keys) as string[]));
      setOffset(0);
    },
    [setSelectedIdsSet, setOffset],
  );

  const handleReset = React.useCallback(() => {
    setSelectedIdsSet(new Set());
    setOffset(0);
  }, [setSelectedIdsSet, setOffset]);

  const getLabel = React.useCallback(
    (item: { id: string; name: string }, isSelected: boolean) => (
      <span className={isSelected ? 'font-medium' : ''}>{item.name}</span>
    ),
    [],
  );

  return (
    <MultiSelectFilter
      className="@PaymentFinancialAccountColumnFilter min-w-[350px]"
      entries={accounts}
      selectedEntries={selectedAccounts}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      getEntryKey={(account) => account.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={handleReset}
      searchPlaceholder={t.payments.list.search.placeholder}
      isLoading={isLoading}
      isFetching={isFetching}
    />
  );
};
